import type { Always } from "types/utilities";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { type Entry, userEntry } from "@/hooks/firestore/models/entry";
import type { SelectionView } from "@/hooks/firestore/models/selection";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { EventStatus } from "@/hooks/firestore/betting/types";
import {
  limit,
  orderBy,
  type QueryConstraint,
  where,
} from "firebase/firestore";
import { sortEntries } from "features/entries/util/entry-helpers";
import { useIsLoggedIn } from "features/auth/hooks/useIsLoggedIn";
import { useIsClient } from "@/hooks/useIsClient";
import { stringifyUrl } from "query-string";
import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApiFetch } from "@/hooks/api/useApiFetch";
import { apiQueryKey } from "@/hooks/api/queryKey";
import type { PickemsSelection } from "../models/pickems";
import { useUserId } from "@/features/auth/hooks/useUserId";

type UseEntryArgs =
  | { entryId?: undefined; shareId: string }
  | { entryId: string; shareId?: undefined };

const useEntry = ({ entryId, shareId }: UseEntryArgs) => {
  const firestorePath = entryId
    ? `/users/{userId}/entries/${entryId}`
    : shareId
      ? `/entryShares/${shareId}`
      : null;

  const {
    data,
    error,
    isLoading: entryLoading,
  } = useFirestore(firestorePath, userEntry, {
    keyPath: `id`,
  });

  const entry = useMemo(() => {
    if (!data) return;

    return processEntry(data);
  }, [data]);

  return {
    entry,
    entryLoading,
    entryError: error,
  };
};

type UseEntriesArgs = {
  tab: string | string[];
  perPage?: number;
  page?: number;
  limitToEntryType?: "BET" | "PICKEM";
};

const useEntries = ({
  tab,
  perPage = 15,
  page = 1,
  limitToEntryType,
}: UseEntriesArgs) => {
  const [loading, setLoading] = useState(true);

  const tabList = typeof tab === "string" ? [tab] : tab;

  const orderByConstraint = orderBy("entryId", "asc");

  const query: QueryConstraint[] = [
    where("status", "in", tabList),
    orderByConstraint,
  ];

  if (typeof limitToEntryType === "string") {
    query.push(where("entryType", "==", limitToEntryType));
  }

  if (Number.isFinite(perPage)) {
    query.push(limit(page * perPage));
  }

  const {
    data,
    error,
    isLoading: entriesLoading,
  } = useFirestore(`/users/{userId}/entries`, userEntry.array(), {
    keyPath: `id`,
    query,
  });

  const entries = useMemo(() => {
    if (entriesLoading) {
      if (page === 1) {
        setLoading(true);
      }
      return;
    }

    if (!data) {
      setLoading(false);
      return [];
    }

    const processedData = data.map((entry) => processEntry(entry));

    setLoading(false);

    return sortEntries(processedData, tab);
  }, [data]);

  return {
    entries,
    loading,
    entryError: error,
  };
};

export const useStaticEntries = (limit: number) => {
  const isLoggedIn = useIsLoggedIn();
  const isClient = useIsClient();
  const userId = useUserId();
  const apiFetch = useApiFetch();
  const queryClient = useQueryClient();

  const queryKey = apiQueryKey("entries/historical", { userId, limit });

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      apiFetch<Entry[]>(
        stringifyUrl({
          url: `${process.env.GATSBY_API_URI}/entries/historical/`,
          query: pageParam ? { limit, startAt: pageParam } : { limit },
        }),
      ),
    initialPageParam: "",
    getNextPageParam: (lastPage) =>
      Array.isArray(lastPage) && lastPage.length === limit
        ? lastPage[lastPage.length - 1].updatedAt
        : undefined,
    enabled: isLoggedIn && isClient,
    // a focus refetch would replay every loaded page
    refetchOnWindowFocus: false,
    meta: { userScoped: true },
  });

  const isLoadingOnClient = isClient ? isLoading : true;
  // error payloads aren't arrays; skip them like the old `!page.errors` filter
  const entries =
    data?.pages.filter((page) => Array.isArray(page)).flat() ?? [];
  const hasMore = Boolean(hasNextPage);

  const reset = () => {
    queryClient.setQueryData<InfiniteData<Entry[], string>>(
      queryKey,
      (current) =>
        current && {
          pages: current.pages.slice(0, 1),
          pageParams: current.pageParams.slice(0, 1),
        },
    );
    refetch({ cancelRefetch: false });
  };
  const getNext = () => fetchNextPage({ cancelRefetch: false });

  const mappedEntries = useMemo(() => {
    return (
      entries
        .map(processEntry)
        .sort((a, b) => (a?.updatedAt > b?.updatedAt ? -1 : 1)) || []
    );
  }, [entries]);

  return {
    entries: mappedEntries,
    getNext,
    reset,
    hasMore,
    isFetchingNext: isFetchingNextPage,
    isLoading: isLoadingOnClient,
  };
};

export const useStaticEntry = (entryId: string) => {
  const userId = useUserId();
  const apiFetch = useApiFetch();

  const { data, isLoading } = useQuery({
    queryKey: apiQueryKey("entries", { userId, entryId }),
    queryFn: () =>
      apiFetch<Entry>(`${process.env.GATSBY_API_URI}/entries/${entryId}`),
    enabled: Boolean(userId && entryId),
    meta: { userScoped: true },
  });

  const entry = useMemo(() => {
    return !isLoading && data ? processEntry(data) : null;
  }, [data, isLoading]);

  return [entry, isLoading] as const;
};

export const useStaticEntriesByEventId = (limit: number, eventId: string) => {
  const userId = useUserId();
  const apiFetch = useApiFetch();

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: apiQueryKey("entries/historical", { userId, limit, eventId }),
    queryFn: () =>
      apiFetch<Entry[]>(
        stringifyUrl({
          url: `${process.env.GATSBY_API_URI}/entries/historical/`,
          query: { limit, eventId },
        }),
      ),
    enabled: Boolean(userId),
    meta: { userScoped: true },
  });

  // refetch() bypasses `enabled` — don't fire unauthenticated requests from
  // logged-out event pages
  const fetchData = useCallback(() => {
    if (userId) refetch({ cancelRefetch: false });
  }, [userId, refetch]);

  const entries = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return (
      (Array.isArray(data) ? data : [])
        ?.map(processEntry)
        ?.sort((a, b) => (a?.updatedAt > b?.updatedAt ? -1 : 1)) ?? []
    );
  }, [data, error]);

  return {
    entries,
    isLoading,
    fetchData,
  };
};

export const useEventEntries = (
  eventId: string,
  eventStatus: EventStatus | undefined,
): [ProcessedEntry[], boolean] => {
  const [entries, setEntries] = useState<ProcessedEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const query: QueryConstraint[] = [
    where("data.eventIds", "array-contains", eventId),
  ];

  // pending or upcoming entries
  const {
    data: entriesRaw,
    isLoading: entriesLoading,
    error,
  } = useFirestore(`/users/{userId}/entries`, userEntry.array(), {
    keyPath: `id`,
    query,
  });

  // settled entries
  const {
    entries: staticEntries,
    isLoading: staticEntriesLoading,
    fetchData,
  } = useStaticEntriesByEventId(100, eventId);

  if (error) {
    // NOTE: this will cause missing indexes end up in sentry
    console.error(error);
  }

  useEffect(() => {
    fetchData();
    // entriesRaw.length is here in the case of cashed out bets moving to settled
  }, [eventStatus, eventId, entriesRaw?.length]);

  useEffect(() => {
    if (entriesLoading || staticEntriesLoading) {
      return;
    }

    const processedEntries = (entriesRaw ?? []).map(processEntry);

    const mergedEntries = [...processedEntries, ...(staticEntries ?? [])];

    const uniqueEntries = Array.from(
      mergedEntries
        .reduce(
          (acc, entry) => acc.set(entry.id, entry),
          new Map<string, ProcessedEntry>(),
        )
        .values(),
    );

    setEntries(sortEntries(uniqueEntries, ["live", "upcoming"]));
  }, [entriesRaw, entriesLoading, staticEntries, staticEntriesLoading]);

  useEffect(() => {
    setLoading(entriesLoading || staticEntriesLoading);
  }, [entriesLoading, staticEntriesLoading]);

  return [entries, loading];
};

export const useLiveEntriesCount = (limitToType?: "BET" | "PICKEM"): number => {
  const { entries } = useEntries({
    tab: "live",
    page: 1,
    perPage: Number.POSITIVE_INFINITY,
    limitToEntryType: limitToType,
  });

  return (entries || []).length;
};

export const useUpcomingEntriesCount = (
  limitToType?: "BET" | "PICKEM",
): number => {
  const { entries } = useEntries({
    tab: "upcoming",
    page: 1,
    perPage: Number.POSITIVE_INFINITY,
    limitToEntryType: limitToType,
  });

  return (entries || []).length;
};

export const usePendingBetsCount = (limitToType?: "BET" | "PICKEM"): number => {
  const live = useLiveEntriesCount(limitToType);
  const upcoming = useUpcomingEntriesCount(limitToType);

  return live + upcoming;
};

export const useLivePickemsEntries = () => {
  const { entries, loading, entryError } = useEntries({
    limitToEntryType: "PICKEM",
    tab: ["live", "upcoming"],
  });

  return { entries, isLoading: loading, error: entryError } as const;
};

const processEntry = (entry: Entry) => {
  const isBet = entry.entryType === "BET";
  const selectionsArray = isBet ? entry.data.selections : [];
  const selections: SelectionView[] | PickemsSelection[] = Object.values(
    entry.data.selectionsMap ?? selectionsArray ?? {},
  );

  const selectionsWithOriginId: ((SelectionView | PickemsSelection) & {
    originId?: string;
  })[] = entry.shareId
    ? selections.map((selection) => ({
        ...selection,
        originId: entry.shareId,
      }))
    : selections;

  // Make changes to bet entry data here
  if (isBet) {
    return {
      ...entry,
      id: `B-${entry.data.entryId}`,
      data: {
        ...entry.data,
        selections: selectionsWithOriginId as (SelectionView & {
          originId?: string;
        })[],
      },
    };
  }

  // Make changes to pickem entry data here
  return {
    ...entry,
    id: `P-${entry.data.contestNumber}-${entry.entryId}`,
    data: {
      ...entry.data,
      name: entry.data.contestTitle ?? "Pick'ems Contest",
      selections: selectionsWithOriginId as (PickemsSelection & {
        originId?: string;
      })[],
      payOut: entry.data.paidOut || entry.data.potentialPayout || 0,
    },
  };
};

type ProcessedEntry = Always<ReturnType<typeof useEntry>["entry"]>;
type ProcessedBetEntry = Extract<ProcessedEntry, { entryType: "BET" }>;
type ProcessedPickemEntry = Extract<ProcessedEntry, { entryType: "PICKEM" }>;

type ProcessedSelection = ProcessedBetEntry["data"]["selections"][0];
type ProcessedSingleSelection = Extract<
  ProcessedSelection,
  { type: "SingleOutcome" }
>;
type ProcessedSameEventMultiSelection = Extract<
  ProcessedSelection,
  { type: "SameEventMulti" }
>;
type ProcessedExoticMultiSelection = Extract<
  ProcessedSelection,
  { type: "Exotic" }
>;
type ProcessedQuaddieSelection = Extract<
  ProcessedSelection,
  { type: "Quaddie" }
>;
type ProcessedPickemSelection = ProcessedPickemEntry["data"]["selections"][0];

type ProcessedSameEventMultiOutcome =
  ProcessedSameEventMultiSelection["outcomes"][0];

type ProcessedCompetitor = Always<ProcessedSingleSelection["competitor"]>;
type ProcessedFranchiseCompetitor = Extract<
  ProcessedCompetitor,
  { type: "FRANCHISE" }
>;
type ProcessedPlayerCompetitor = Extract<
  ProcessedCompetitor,
  { type: "PLAYER" }
>;
type ProcessedRacingCompetitor = Exclude<ProcessedCompetitor, { type: any }>;

type ProcessedEntries = Always<ReturnType<typeof useEntries>["entries"]>;

export type {
  ProcessedEntry,
  ProcessedBetEntry,
  ProcessedPickemEntry,
  ProcessedSelection,
  ProcessedSingleSelection,
  ProcessedSameEventMultiSelection,
  ProcessedExoticMultiSelection,
  ProcessedQuaddieSelection,
  ProcessedPickemSelection,
  ProcessedEntries,
  ProcessedSameEventMultiOutcome,
  ProcessedCompetitor,
  ProcessedFranchiseCompetitor,
  ProcessedPlayerCompetitor,
  ProcessedRacingCompetitor,
};

export { useEntry, useEntries };
